import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import {
  Ban,
  DollarSign,
  Loader2,
  Package,
  Settings as SettingsIcon,
  ShieldAlert,
  Trash2,
  UserCog,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { api } from '@/api/client';

const ALL = '__all__';

/**
 * How each action reads to a person, and how prominent it should be.
 *
 * Money-moving actions get red or amber; configuration changes stay grey. The
 * point of the page is that "who refunded this label" is answerable at a
 * glance, so those entries cannot look like a settings tweak.
 */
const ACTION_STYLES = {
  'label.purchased': { label: 'Bought a label', icon: Package, tone: 'money' },
  'label.refunded': { label: 'Refunded a label', icon: DollarSign, tone: 'alert' },
  'label.imported': { label: 'Imported labels from EasyPost', icon: Package, tone: 'quiet' },
  'scanform.created': { label: 'Created a scan form', icon: Package, tone: 'quiet' },
  'batch.started': { label: 'Started a bulk run', icon: Package, tone: 'money' },
  'batch.cancelled': { label: 'Cancelled a bulk run', icon: Ban, tone: 'alert' },
  'order.deleted': { label: 'Deleted an order', icon: Trash2, tone: 'alert' },
  'order.archived': { label: 'Archived an order', icon: Package, tone: 'quiet' },
  'order.unarchived': { label: 'Restored an order', icon: Package, tone: 'quiet' },
  'settings.updated': { label: 'Changed settings', icon: SettingsIcon, tone: 'quiet' },
  'user.created': { label: 'Added a user', icon: UserCog, tone: 'admin' },
  'user.updated': { label: 'Changed a user', icon: UserCog, tone: 'admin' },
  'user.passwordReset': { label: 'Reset a password', icon: ShieldAlert, tone: 'admin' },
  'user.banned': { label: 'Disabled an account', icon: Ban, tone: 'admin' },
  'user.unbanned': { label: 'Re-enabled an account', icon: UserCog, tone: 'admin' },
};

const TONES = {
  money: 'bg-blue-50 text-blue-700 border-blue-200',
  alert: 'bg-red-50 text-red-700 border-red-200',
  admin: 'bg-purple-50 text-purple-700 border-purple-200',
  quiet: 'bg-slate-50 text-slate-600 border-slate-200',
};

export default function ActivityPage() {
  const [filters, setFilters] = useState({ actorId: ALL, action: ALL, moneyOnly: false });

  const { data: filterOptions } = useQuery({
    queryKey: ['auditFilters'],
    queryFn: () => api.request('/audit/filters'),
  });

  const buildQuery = (cursor) => {
    const params = new URLSearchParams();
    if (filters.actorId !== ALL) params.set('actorId', filters.actorId);
    if (filters.action !== ALL) params.set('action', filters.action);
    if (filters.moneyOnly) params.set('moneyOnly', 'true');
    if (cursor) params.set('cursor', cursor);
    return params.toString();
  };

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['audit', filters],
    queryFn: ({ pageParam }) => api.request(`/audit?${buildQuery(pageParam)}`),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const entries = data?.pages.flatMap((page) => page.entries) ?? [];

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Activity</h1>
        <p className="text-slate-600">Who did what, and when</p>
      </div>

      <Card className="border-none shadow-lg">
        <CardContent className="p-4 flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <Label className="text-xs">Person</Label>
            <Select
              value={filters.actorId}
              onValueChange={(value) => setFilters({ ...filters, actorId: value })}
            >
              <SelectTrigger className="w-48 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Everyone</SelectItem>
                {(filterOptions?.actors ?? []).map((actor) => (
                  <SelectItem key={actor.id} value={actor.id}>
                    {actor.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Action</Label>
            <Select
              value={filters.action}
              onValueChange={(value) => setFilters({ ...filters, action: value })}
            >
              <SelectTrigger className="w-56 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Anything</SelectItem>
                {(filterOptions?.actions ?? []).map((entry) => (
                  <SelectItem key={entry.action} value={entry.action}>
                    {ACTION_STYLES[entry.action]?.label ?? entry.action} ({entry.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2 pb-1">
            <Switch
              id="money-only"
              checked={filters.moneyOnly}
              onCheckedChange={(checked) => setFilters({ ...filters, moneyOnly: checked })}
            />
            <Label htmlFor="money-only" className="cursor-pointer">
              Only things that moved money
            </Label>
          </div>
        </CardContent>
      </Card>

      <Card className="border-none shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-slate-100 to-slate-50">
          <CardTitle>History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : entries.length === 0 ? (
            <p className="p-12 text-center text-slate-500">Nothing matches those filters.</p>
          ) : (
            <ul className="divide-y">
              {entries.map((entry) => (
                <li key={entry.id}>
                  <AuditEntry entry={entry} />
                </li>
              ))}
            </ul>
          )}

          {hasNextPage && (
            <div className="p-4 border-t">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading…
                  </>
                ) : (
                  'Show older'
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AuditEntry({ entry }) {
  const style = ACTION_STYLES[entry.action] ?? {
    label: entry.action,
    icon: SettingsIcon,
    tone: 'quiet',
  };
  const Icon = style.icon;
  const when = new Date(entry.createdAt);

  return (
    <div className="flex items-start gap-4 p-4 hover:bg-slate-50">
      <div className={`w-9 h-9 rounded-lg border flex items-center justify-center shrink-0 ${TONES[style.tone]}`}>
        <Icon className="w-4 h-4" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-semibold text-slate-900">
            {entry.actor?.name ?? 'The system'}
          </span>
          <span className="text-slate-600">{style.label.toLowerCase()}</span>
          {style.tone === 'alert' && <Badge variant="destructive">money</Badge>}
        </div>

        <Summary entry={entry} />

        <p
          className="text-xs text-slate-400 mt-1"
          title={`${formatInTimeZone(when, 'America/New_York', 'PPpp')}${entry.ipAddress ? ` · from ${entry.ipAddress}` : ''}`}
        >
          {formatDistanceToNow(when, { addSuffix: true })}
        </p>
      </div>
    </div>
  );
}

/**
 * One line describing what actually changed.
 *
 * The raw before/after JSON is stored and kept, but showing it here would bury
 * the answer. Each action gets the two or three fields someone would actually
 * be looking for.
 */
function Summary({ entry }) {
  const after = entry.after ?? {};
  const before = entry.before ?? {};

  if (entry.action === 'label.purchased') {
    return (
      <p className="text-sm text-slate-600">
        {after.recipient} · {after.carrier} {after.service} ·{' '}
        <span className="font-semibold">${Number(after.cost ?? 0).toFixed(2)}</span>
        {after.tracking && <span className="font-mono text-xs ml-2">{after.tracking}</span>}
      </p>
    );
  }

  if (entry.action === 'label.refunded') {
    return (
      <p className="text-sm text-slate-600">
        {before.tracking && <span className="font-mono text-xs mr-2">{before.tracking}</span>}
        {before.cost != null && <>${Number(before.cost).toFixed(2)} · </>}
        {after.refundSucceeded || after.succeeded ? (
          <span className="text-green-700">refund accepted</span>
        ) : (
          <span className="text-red-700">
            refund not accepted{after.error ? ` — ${after.error}` : ''}
          </span>
        )}
      </p>
    );
  }

  if (entry.action === 'order.deleted') {
    return (
      <p className="text-sm text-slate-600">
        {before.orderName} ({before.orderNumber}) · {before.parcels} parcels · $
        {Number(before.totalCost ?? 0).toFixed(2)} ·{' '}
        {after.refundsSucceeded ?? 0}/{after.refundsRequested ?? 0} refunded
      </p>
    );
  }

  if (entry.action === 'batch.started') {
    return (
      <p className="text-sm text-slate-600">
        {after.orderName ? `${after.orderName} · ` : ''}
        {after.rows ?? after.items ?? 0} rows
        {after.strategy === 'CHEAPEST_PER_PARCEL' ? ' · cheapest per parcel' : ''}
        {after.provider ? ` · ${after.provider} ${after.service ?? ''}` : ''}
      </p>
    );
  }

  if (entry.entity === 'User') {
    return (
      <p className="text-sm text-slate-600">
        {after.email ?? before.email ?? ''}
        {after.role && before.role && after.role !== before.role
          ? ` · ${before.role} → ${after.role}`
          : ''}
      </p>
    );
  }

  if (entry.action === 'settings.updated') {
    const changed = Object.keys(after).filter(
      (key) => !['id', 'createdAt', 'updatedAt'].includes(key) && after[key] !== before[key],
    );
    return changed.length > 0 ? (
      <p className="text-sm text-slate-600">changed {changed.join(', ')}</p>
    ) : null;
  }

  const name = after.name ?? before.name;
  return name ? <p className="text-sm text-slate-600">{name}</p> : null;
}
